class CtmMobileNavA11y {
  constructor(host) {
    this.host = host;
    this.content = host.content;
    this.menuBtn = host.menuBtn;
    this.closeBtn = host.closeBtn;
    this.focusReturnEl = null;

    this.onEscapeKey = (event) => {
      if (!this.host.classList.contains('is-open') || event.key !== 'Escape') return;

      event.preventDefault();
      event.stopPropagation();
      this.host.close();
    };
    this.onDocumentKeyDown = (event) => {
      if (!this.host.classList.contains('is-open') || event.key !== 'Tab') return;

      const focusables = this.getFocusableElements();
      if (!focusables.length) {
        event.preventDefault();
        this.closeBtn?.focus();
        return;
      }

      const activeIndex = focusables.indexOf(document.activeElement);
      if (activeIndex === -1) {
        event.preventDefault();
        focusables[0].focus();
        return;
      }

      if (event.shiftKey && activeIndex === 0) {
        event.preventDefault();
        focusables[focusables.length - 1].focus();
      } else if (!event.shiftKey && activeIndex === focusables.length - 1) {
        event.preventDefault();
        focusables[0].focus();
      }
    };
  }

  connect() {
    window.addEventListener('keydown', this.onEscapeKey, true);
    window.addEventListener('keyup', this.onEscapeKey, true);
    document.addEventListener('keydown', this.onDocumentKeyDown);
  }

  disconnect() {
    window.removeEventListener('keydown', this.onEscapeKey, true);
    window.removeEventListener('keyup', this.onEscapeKey, true);
    document.removeEventListener('keydown', this.onDocumentKeyDown);
  }

  captureFocusReturn() {
    this.focusReturnEl = document.activeElement instanceof HTMLElement ? document.activeElement : this.menuBtn;
  }

  setDrawerExpanded(expanded) {
    const value = expanded ? 'true' : 'false';
    this.menuBtn.setAttribute('aria-expanded', value);
    this.closeBtn.setAttribute('aria-expanded', value);
  }

  setTabActive(button, active) {
    button.setAttribute('aria-pressed', active ? 'true' : 'false');
  }

  setPanelActive(panel, active) {
    panel.hidden = !active;
  }

  setSupportSectionExpanded(section, expanded) {
    const toggle = section.querySelector('.ctm-mobile-support-links__toggle');
    const list = section.querySelector('.ctm-mobile-support-links__list');
    if (!toggle || !list) return false;

    toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    list.hidden = !expanded;
    return true;
  }

  getSupportSectionDefaultExpanded(section) {
    return section.dataset.defaultExpanded === 'true';
  }

  getFocusableElements(container = this.content) {
    if (!container) return [];

    const selectors = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ];

    const focusables = Array.from(container.querySelectorAll(selectors.join(','))).filter((element) => {
      if (!(element instanceof HTMLElement)) return false;
      if (element.hasAttribute('hidden')) return false;
      if (element.closest('[hidden]')) return false;
      if (element.getAttribute('aria-hidden') === 'true') return false;
      return element.offsetParent !== null;
    });

    if (this.closeBtn && !this.closeBtn.hasAttribute('hidden')) {
      const closeBtnStyle = window.getComputedStyle(this.closeBtn);
      if (closeBtnStyle.display !== 'none' && closeBtnStyle.visibility !== 'hidden') {
        focusables.push(this.closeBtn);
      }
    }

    return [...new Set(focusables)];
  }

  focusFirstVisibleElement() {
    const activePanel = this.host.querySelector('.ctm-mobile-nav-panel.is-active:not([hidden])');
    const focusables = this.getFocusableElements(activePanel || this.content);
    (focusables[0] || this.closeBtn)?.focus();
  }

  restoreFocus() {
    if (!this.focusReturnEl) return;

    const focusTarget = document.contains(this.focusReturnEl) ? this.focusReturnEl : this.menuBtn;
    focusTarget?.focus();
    this.focusReturnEl = null;
  }
}

class CtmMobileNav extends HTMLElement {
  static TRANSITION_MS = 450;

  static lockScroll(locked) {
    document.documentElement.classList.toggle('ctm-lock-scroll', locked);
  }

  static setHeaderOverlayState(overlay = 'none') {
    const headerEl = document.querySelector('#ctm-header');
    if (!headerEl) return;
    headerEl.dataset.overlay = overlay || 'none';
  }

  connectedCallback() {
    this.mask = this.querySelector('.ctm-mobile-nav__mask');
    this.menuBtn = document.querySelector('.ctm-nav-menu');
    this.closeBtn = document.querySelector('.ctm-nav-close');
    this.content = this.querySelector('.ctm-mobile-nav__content');
    this.innerCloseBtn = this.querySelector('.ctm-mobile-nav__panel-close');
    this.defaultTab = this.dataset.defaultTab || 'shop';
    this.tabBtns = Array.from(this.querySelectorAll('[data-mobile-tab-trigger]'));
    this.tabPanels = Array.from(this.querySelectorAll('.ctm-mobile-nav-panel'));
    this.supportLinks = Array.from(this.querySelectorAll('.ctm-mobile-support-links'));

    if (!this.mask || !this.menuBtn || !this.closeBtn || !this.content) return;

    this.a11y = new CtmMobileNavA11y(this);

    this.onMenuClick = () => this.open();
    this.onCloseClick = () => this.close();
    this.onMaskClick = () => this.close();
    this.onSupportToggleClick = (event) => {
      const toggle = event.target.closest('.ctm-mobile-support-links__toggle');
      if (!toggle || !this.contains(toggle)) return;
      event.preventDefault();
      const section = toggle.closest('.ctm-mobile-support-links');
      if (!section) return;
      this.toggleSupportSection(section);
    };
    this.onResize = () => {
      this.updateHeight();
      if (window.innerWidth > 960) this.close(true);
    };

    this.menuBtn.addEventListener('click', this.onMenuClick);
    this.closeBtn.addEventListener('click', this.onCloseClick);
    if (this.innerCloseBtn) this.innerCloseBtn.addEventListener('click', this.onCloseClick);
    this.mask.addEventListener('click', this.onMaskClick);
    this.addEventListener('click', this.onSupportToggleClick);
    window.addEventListener('resize', this.onResize);
    this.a11y.connect();

    this.initTabs();
    this.initSupportLinks();
    this.close(true);
  }

  disconnectedCallback() {
    if (this.menuBtn && this.onMenuClick) this.menuBtn.removeEventListener('click', this.onMenuClick);
    if (this.closeBtn && this.onCloseClick) this.closeBtn.removeEventListener('click', this.onCloseClick);
    if (this.innerCloseBtn && this.onCloseClick) this.innerCloseBtn.removeEventListener('click', this.onCloseClick);
    if (this.mask && this.onMaskClick) this.mask.removeEventListener('click', this.onMaskClick);
    if (this.onSupportToggleClick) this.removeEventListener('click', this.onSupportToggleClick);
    if (this.onResize) window.removeEventListener('resize', this.onResize);
    this.a11y?.disconnect();
    this.unbindTabs();
  }

  updateHeight() {
    if (window.innerWidth <= 960) {
      this.style.top = '0px';
      this.style.height = '100dvh';
      this.updateMobileStickyOffsets();
      return;
    }
    const header = document.querySelector('#ctm-header');
    const headerRect = header ? header.getBoundingClientRect() : null;
    const top = headerRect ? Math.max(headerRect.bottom, 0) : 0;
    this.style.top = `${top}px`;
    this.style.height = `calc(100vh - ${top}px)`;
  }

  updateMobileStickyOffsets() {
    const brand = this.querySelector('.ctm-mobile-nav__brand');
    if (!brand) return;
    const brandHeight = Math.round(brand.getBoundingClientRect().height);
    if (!brandHeight) return;
    this.style.setProperty('--ctm-mobile-brand-height', `${brandHeight}px`);
  }

  open() {
    if (window.innerWidth > 960) return;

    this.a11y.captureFocusReturn();
    this.updateHeight();
    this.style.top = '0px';
    this.style.left = '0px';
    this.style.right = '0px';
    this.style.bottom = '0px';
    this.style.height = '100dvh';
    this.updateMobileStickyOffsets();
    this.classList.add('ctm-show');

    window.requestAnimationFrame(() => {
      this.classList.add('is-open');
      this.a11y.focusFirstVisibleElement();
    });

    this.menuBtn.classList.add('is-open');
    this.closeBtn.classList.add('is-open');
    this.a11y.setDrawerExpanded(true);
    document.documentElement.classList.add('ctm-mobile-nav-open');
    CtmMobileNav.setHeaderOverlayState('mobile-nav-open');
    CtmMobileNav.lockScroll(true);
  }

  close(immediate = false) {
    this.classList.remove('is-open');
    this.closeBtn.classList.remove('is-open');
    this.menuBtn.classList.remove('is-open');
    this.a11y.setDrawerExpanded(false);
    document.documentElement.classList.remove('ctm-mobile-nav-open');
    CtmMobileNav.setHeaderOverlayState(document.querySelector('ctm-search.is-open') ? 'search-open' : 'none');
    CtmMobileNav.lockScroll(false);

    const header = document.querySelector('#ctm-header');
    if (header && typeof header.updateTransparentState === 'function') {
      header.classList.remove('ctm-header--interactive');
      const scrollTop = Math.max(window.pageYOffset || document.documentElement.scrollTop || 0, 0);
      header.updateTransparentState(scrollTop);
    }

    this.dispatchEvent(new CustomEvent('ctm-mobile-nav:reset', { bubbles: true }));
    this.switchTab(this.defaultTab);
    this.resetSupportLinks();

    if (immediate) {
      this.classList.remove('ctm-show');
      this.a11y.restoreFocus();
      return;
    }

    window.setTimeout(() => {
      if (!this.classList.contains('is-open')) {
        this.classList.remove('ctm-show');
        this.a11y.restoreFocus();
      }
    }, CtmMobileNav.TRANSITION_MS);
  }

  initTabs() {
    this.onTabClickHandlers = [];
    this.tabBtns.forEach((btn) => {
      const tab = btn.dataset.mobileTabTrigger;
      const handler = () => this.switchTab(tab);
      btn.addEventListener('click', handler);
      this.onTabClickHandlers.push({ btn, handler });
    });
    this.switchTab(this.defaultTab);
  }

  unbindTabs() {
    if (!this.onTabClickHandlers) return;
    this.onTabClickHandlers.forEach(({ btn, handler }) => btn.removeEventListener('click', handler));
    this.onTabClickHandlers = [];
  }

  switchTab(tabName) {
    const activeTab = tabName || 'shop';
    this.tabBtns.forEach((btn) => {
      const isActive = btn.dataset.mobileTabTrigger === activeTab;
      btn.classList.toggle('is-active', isActive);
      this.a11y.setTabActive(btn, isActive);
    });

    this.tabPanels.forEach((panel) => {
      const panelTab = panel.dataset.mobileTab || 'shop';
      const isActive = panelTab === activeTab;
      panel.classList.toggle('is-active', isActive);
      this.a11y.setPanelActive(panel, isActive);
    });

    this.resetSupportLinks();
  }

  initSupportLinks() {
    this.resetSupportLinks();
  }

  resetSupportLinks() {
    this.supportLinks.forEach((section) => {
      const expanded = this.a11y.getSupportSectionDefaultExpanded(section);
      if (this.a11y.setSupportSectionExpanded(section, expanded)) {
        section.classList.toggle('is-open', expanded);
      }
    });
  }

  toggleSupportSection(section) {
    const isOpen = !section.classList.contains('is-open');
    if (this.a11y.setSupportSectionExpanded(section, isOpen)) {
      section.classList.toggle('is-open', isOpen);
    }
  }
}
customElements.define('ctm-mobile-nav', CtmMobileNav);

class CtmMobileNavLink extends HTMLElement {
  static TRANSITION_MS = 450;

  connectedCallback() {
    this.mobileNav = this.closest('ctm-mobile-nav');
    this.titleEl = this.querySelector('.ctm-mobile-nav-link-title');
    this.titleBtn = this.querySelector('.ctm-mobile-nav-link-title-btn');
    this.contentEl = this.querySelector('.ctm-mobile-nav-link-content');
    this.backEl = this.querySelector('.ctm-mobile-nav-link-back');
    this.closeEl = this.querySelector('.ctm-mobile-nav-link-close');
    this.childToggles = Array.from(this.querySelectorAll('.ctm-mobile-nav-link-content-list'));

    if (this.contentEl && this.titleBtn) {
      this.onTitleClick = (event) => {
        if (event.target.closest('.ctm-mobile-nav-link-href')) return;
        this.openContent();
      };
      this.titleBtn.addEventListener('click', this.onTitleClick);
    }

    if (this.backEl) {
      this.onBackClick = () => this.closeContent();
      this.backEl.addEventListener('click', this.onBackClick);
    }

    if (this.closeEl) {
      this.onCloseClick = () => {
        const mobileNav = this.closest('ctm-mobile-nav');
        if (mobileNav && typeof mobileNav.close === 'function') {
          mobileNav.close();
        }
      };
      this.closeEl.addEventListener('click', this.onCloseClick);
    }

    this.imageLoadListeners = [];

    this.childToggles.forEach((toggle) => {
      const parent = toggle.closest('.ctm-mobile-nav-link-childs');
      const panel = parent ? parent.querySelector('.ctm-mobile-nav-link-child') : null;
      if (!parent || !panel) return;

      toggle.addEventListener('click', (event) => {
        if (event.target.tagName === 'A') return;
        this.toggleChild(parent, panel, event.currentTarget);
      });

      const images = Array.from(panel.querySelectorAll('img'));
      images.forEach((img) => {
        const onImageLoad = () => this.syncOpenPanelHeight(parent, panel);
        if (!img.complete) img.addEventListener('load', onImageLoad);
        img.addEventListener('error', onImageLoad);
        this.imageLoadListeners.push({ img, onImageLoad });
      });
    });

    this.applyDefaultChildState();

    this.onNavReset = () => this.reset();
    this.addEventListener('ctm-mobile-nav:reset', this.onNavReset);
  }

  disconnectedCallback() {
    if (this.titleBtn && this.onTitleClick) this.titleBtn.removeEventListener('click', this.onTitleClick);
    if (this.backEl && this.onBackClick) this.backEl.removeEventListener('click', this.onBackClick);
    if (this.closeEl && this.onCloseClick) this.closeEl.removeEventListener('click', this.onCloseClick);
    if (this.onNavReset) this.removeEventListener('ctm-mobile-nav:reset', this.onNavReset);
    if (this.imageLoadListeners && this.imageLoadListeners.length) {
      this.imageLoadListeners.forEach(({ img, onImageLoad }) => {
        img.removeEventListener('load', onImageLoad);
        img.removeEventListener('error', onImageLoad);
      });
      this.imageLoadListeners = [];
    }
  }

  openContent() {
    if (!this.contentEl) return;
    this.classList.add('is-subpage-open');
    if (this.mobileNav) this.mobileNav.classList.add('has-subpage-open');
    if (this.titleBtn) this.titleBtn.setAttribute('aria-expanded', 'true');
    this.contentEl.hidden = false;
    this.contentEl.setAttribute('aria-hidden', 'false');
    this.contentEl.style.display = 'flex';
    window.requestAnimationFrame(() => {
      this.contentEl.classList.add('is-open');
      this.applyDefaultChildState();
      this.focusFirstVisibleChild();
    });
  }

  closeContent() {
    if (!this.contentEl) return;
    this.contentEl.classList.remove('is-open');
    this.classList.remove('is-subpage-open');
    if (this.titleBtn) this.titleBtn.setAttribute('aria-expanded', 'false');
    this.contentEl.setAttribute('aria-hidden', 'true');
    window.setTimeout(() => {
      if (!this.contentEl.classList.contains('is-open')) {
        this.contentEl.style.display = 'none';
        this.contentEl.hidden = true;
      }
      if (this.mobileNav && !this.mobileNav.querySelector('.ctm-mobile-nav-link.is-subpage-open')) {
        this.mobileNav.classList.remove('has-subpage-open');
      }
      if (this.mobileNav && this.mobileNav.classList.contains('is-open') && this.titleBtn) {
        this.titleBtn.focus();
      }
    }, CtmMobileNavLink.TRANSITION_MS);
  }

  toggleChild(parent, panel, toggleButton) {
    const isOpen = parent.classList.contains('is-open');
    const layoutMode = panel.dataset.layout || 'list';
    const openDisplay = layoutMode === 'grid-2' ? 'grid' : 'block';
    if (toggleButton) {
      toggleButton.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
    }
    if (isOpen) {
      parent.classList.remove('is-open');
      if (panel.style.height === 'auto') {
        panel.style.height = `${panel.scrollHeight}px`;
      }
      window.requestAnimationFrame(() => {
        panel.style.height = '0px';
      });
      window.setTimeout(() => {
        if (!parent.classList.contains('is-open')) {
          panel.style.display = 'none';
          panel.hidden = true;
        }
      }, 300);
    } else {
      parent.classList.add('is-open');
      panel.hidden = false;
      panel.style.display = openDisplay;
      panel.style.height = `${panel.scrollHeight}px`;
      window.setTimeout(() => this.syncOpenPanelHeight(parent, panel), 80);
      window.setTimeout(() => {
        if (parent.classList.contains('is-open')) {
          panel.style.height = 'auto';
        }
      }, 300);
    }
  }

  syncOpenPanelHeight(parent, panel) {
    if (!parent.classList.contains('is-open')) return;
    if (panel.style.display === 'none') return;
    if (panel.style.height === 'auto') return;
    panel.style.height = `${panel.scrollHeight}px`;
  }

  reset() {
    this.closeContent();
    this.applyDefaultChildState();
    if (this.mobileNav) this.mobileNav.classList.remove('has-subpage-open');
  }

  applyDefaultChildState() {
    this.querySelectorAll('.ctm-mobile-nav-link-childs').forEach((parent) => {
      const panel = parent.querySelector('.ctm-mobile-nav-link-child');
      const toggleButton = parent.querySelector('.ctm-mobile-nav-link-content-list');
      if (!panel) return;
      const layoutMode = panel.dataset.layout || 'list';
      const openDisplay = layoutMode === 'grid-2' ? 'grid' : 'block';
      const shouldOpen = parent.dataset.defaultExpanded === 'true';

      if (toggleButton) {
        toggleButton.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
      }

      if (shouldOpen) {
        parent.classList.add('is-open');
        panel.hidden = false;
        panel.style.display = openDisplay;
        panel.style.height = 'auto';
      } else {
        parent.classList.remove('is-open');
        panel.style.height = '0px';
        panel.style.display = 'none';
        panel.hidden = true;
      }
    });
  }

  focusFirstVisibleChild() {
    if (!this.contentEl) return;

    const focusables = Array.from(
      this.contentEl.querySelectorAll(
        [
          'a[href]',
          'button:not([disabled])',
          'input:not([disabled])',
          'select:not([disabled])',
          'textarea:not([disabled])',
          '[tabindex]:not([tabindex="-1"])',
        ].join(','),
      ),
    ).filter((element) => {
      if (!(element instanceof HTMLElement)) return false;
      if (element.hasAttribute('hidden')) return false;
      if (element.closest('[hidden]')) return false;
      if (element.getAttribute('aria-hidden') === 'true') return false;
      return element.offsetParent !== null;
    });

    if (focusables.length) {
      focusables[0].focus();
    }
  }
}
customElements.define('ctm-mobile-nav-link', CtmMobileNavLink);

class CtmContactUsPopup extends HTMLElement {
  openLiveChat() {
    if (window.innerWidth < 768) {
      if (window.LC_API && typeof window.LC_API.open_mobile_window === 'function') {
        window.LC_API.open_mobile_window();
      }
      return;
    }

    if (window.LC_API && typeof window.LC_API.open_chat_window === 'function') {
      window.LC_API.open_chat_window();
    }
  }

  connectedCallback() {
    this.querySelectorAll('.chat-now').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.openLiveChat();
      });
    });

    this.serviceMenu = this.querySelector('.ctm-service-menu');
    this.serviceTrigger = this.querySelector('.ctm-service-trigger');
    this.servicePopover = this.querySelector('.ctm-service-popover');
    if (!this.serviceMenu || !this.serviceTrigger || !this.servicePopover) return;

    this.servicePopover.hidden = true;
    this.servicePopover.setAttribute('aria-hidden', 'true');
    this.servicePopover.setAttribute('role', 'region');
    const popoverTitle = this.servicePopover.querySelector('.ctm-service-help__title');
    if (popoverTitle && popoverTitle.id) {
      this.servicePopover.setAttribute('aria-labelledby', popoverTitle.id);
    }

    this.openServicePopover = () => {
      this.serviceMenu.classList.add('is-open');
      this.serviceTrigger.setAttribute('aria-expanded', 'true');
      this.servicePopover.hidden = false;
      this.servicePopover.setAttribute('aria-hidden', 'false');
    };

    this.closeServicePopover = () => {
      this.serviceMenu.classList.remove('is-open');
      this.serviceTrigger.setAttribute('aria-expanded', 'false');
      this.servicePopover.hidden = true;
      this.servicePopover.setAttribute('aria-hidden', 'true');
    };

    this.toggleServicePopover = () => {
      const isOpen = this.serviceMenu.classList.contains('is-open');
      if (isOpen) {
        this.closeServicePopover();
      } else {
        this.openServicePopover();
      }
    };

    this.onServiceTriggerClick = (event) => {
      event.preventDefault();
      this.toggleServicePopover();
    };
    this.onServiceMenuMouseEnter = () => this.openServicePopover();
    this.onServiceMenuFocusIn = () => this.openServicePopover();
    this.onServiceMenuMouseLeave = () => this.closeServicePopover();
    this.onServiceMenuFocusOut = (event) => {
      const nextFocused = event.relatedTarget;
      if (nextFocused && this.serviceMenu.contains(nextFocused)) return;
      this.closeServicePopover();
    };
    this.onDocumentClickForService = (event) => {
      if (!this.serviceMenu.contains(event.target)) this.closeServicePopover();
    };
    this.onDocumentKeyDownForService = (event) => {
      if (event.key !== 'Escape') return;
      this.closeServicePopover();
      this.serviceTrigger.focus();
    };

    this.serviceTrigger.addEventListener('click', this.onServiceTriggerClick);
    this.serviceMenu.addEventListener('mouseenter', this.onServiceMenuMouseEnter);
    this.serviceMenu.addEventListener('focusin', this.onServiceMenuFocusIn);
    this.serviceMenu.addEventListener('mouseleave', this.onServiceMenuMouseLeave);
    this.serviceMenu.addEventListener('focusout', this.onServiceMenuFocusOut);
    document.addEventListener('click', this.onDocumentClickForService);
    document.addEventListener('keydown', this.onDocumentKeyDownForService);
  }

  disconnectedCallback() {
    if (this.serviceTrigger && this.onServiceTriggerClick) {
      this.serviceTrigger.removeEventListener('click', this.onServiceTriggerClick);
    }
    if (this.serviceMenu && this.onServiceMenuMouseEnter) {
      this.serviceMenu.removeEventListener('mouseenter', this.onServiceMenuMouseEnter);
    }
    if (this.serviceMenu && this.onServiceMenuFocusIn) {
      this.serviceMenu.removeEventListener('focusin', this.onServiceMenuFocusIn);
    }
    if (this.serviceMenu && this.onServiceMenuMouseLeave) {
      this.serviceMenu.removeEventListener('mouseleave', this.onServiceMenuMouseLeave);
    }
    if (this.serviceMenu && this.onServiceMenuFocusOut) {
      this.serviceMenu.removeEventListener('focusout', this.onServiceMenuFocusOut);
    }
    if (this.onDocumentClickForService) {
      document.removeEventListener('click', this.onDocumentClickForService);
    }
    if (this.onDocumentKeyDownForService) {
      document.removeEventListener('keydown', this.onDocumentKeyDownForService);
    }
  }
}
customElements.define('ctm-contact-us-popup', CtmContactUsPopup);
